import React, { useContext, useEffect, useState, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import ReactSelect from 'react-select'

import { UserContext } from '../../wrappers/UserContext'
import {
    checkRole,
    getScopedAgencies,
} from '../../../utils/user/permissions'
import { DataContext } from './DataContext'
import LoadingWrapper from '../../wrappers/LoadingWrapper'
import urls, { createSubmissionUrl } from '../../../utils/constants/urls'
import Breadcrumbs from './form/Breadcrumbs'
import withConfig from '../../wrappers/withConfig'

const AgencyMessage = ({ agency }) => (
    <div className="column is-7">Submitting results as {agency.AgencyName}</div>
)

const FormReviewerSelection = withConfig(
    ({ config, agencies, setAgencyCode }) => {
        const options = agencies.map(agency => ({
            label: agency.AgencyName,
            value: agency.AgencyCode,
        }))
        const { AGENCY_FIELD_NAME } = config

        return (
            <div className="column is-7">
                Please select an {AGENCY_FIELD_NAME} to submit for:
                <div className="">
                    <ReactSelect
                        className="select is-multiple is-small is-multiple is-fullwidth reactSelect"
                        classNamePrefix="reactSelect"
                        options={options}
                        menuPlacement="auto"
                        onChange={option => setAgencyCode(option.value)}
                    />
                </div>
            </div>
        )
    }
)

const ContinueButton = ({ onClick }) => (
    <div className="button is-link is-medium" onClick={onClick}>
        Continue
    </div>
)

const CancelButton = () => {
    const { type } = useParams()
    const { formTypes } = useContext(DataContext)
    const formType = useMemo(
        () => formTypes && formTypes.find(x => x.Link === type),
        [formTypes, type]
    )
    const uploadDialogueUrl = useMemo(
        () =>
            formType && formType.ShowSelectionDialogue
                ? createSubmissionUrl(urls.uploadDialogue, type)
                : urls.uploadSelectionDialogue,
        [formType, type]
    )
    return (
        <Link to={uploadDialogueUrl}>
            <div className="button is-medium">Back</div>
        </Link>
    )
}

export default ({ onSubmit }) => {
    const { roles } = useContext(UserContext)
    const { agencies } = useContext(DataContext)
    const [agencyCode, setAgencyCode] = useState(null)
    const [scopedAgencies, setScopedAgencies] = useState(getScopedAgencies(roles))
    const [submit, setSubmit] = useState(false)
    const [isFormReviewer, setIsFormReviewer] = useState(
        checkRole('Form_Reviewer', roles)
    )
    const [selectedAgency, setSelectedAgency] = useState(null)

    useEffect(() => {
        // THL 11.2.2024 - CWP2-360 - update the LOVs for form creation to those associated with submitter roles
        // and allow multiple agencies allowed in selection dialogue
        setScopedAgencies(getScopedAgencies(roles))
        setIsFormReviewer(checkRole('Form_Reviewer', roles))
    }, [roles])

    useEffect(() => {
        // THL 11.2.2024 - we only want to set the agency code if they only have one submitting role selected.
        if (agencies && scopedAgencies && scopedAgencies.length === 1) {
            setAgencyCode(scopedAgencies[0])
        }
    }, [scopedAgencies, agencies])

    useEffect(() => {
        if (agencies && agencyCode) {
            setSelectedAgency(
                agencies.find(agency => agency.AgencyCode === agencyCode)
            )
        }
    }, [agencyCode, agencies])

    useEffect(() => {
        if (submit) {
            onSubmit(agencyCode)
        }
    }, [submit, agencyCode])

    return (
        <div className="hero is-fullheight-with-navbar">
            <Breadcrumbs />
            <div className="hero-body">
                <div className="container">
                    <div className="section">
                        <div className="columns is-centered is-multiline">
                            <LoadingWrapper data={agencies}>
                                {isFormReviewer && agencies ? (
                                    <FormReviewerSelection
                                        agencies={agencies}
                                        setAgencyCode={setAgencyCode}
                                    />
                                ) : null}
                                {!isFormReviewer && scopedAgencies && agencies && scopedAgencies.length ? (
                                    scopedAgencies.length === 1 && selectedAgency ? (
                                        <AgencyMessage
                                            agency={selectedAgency}
                                        />
                                    ) : (
                                        <FormReviewerSelection
                                            agencies={agencies.filter(agency =>
                                                scopedAgencies.includes(agency.AgencyCode)
                                            )}
                                            setAgencyCode={setAgencyCode}
                                            selectedAgency={selectedAgency}
                                        />
                                    )
                                ) : null}
                                <div className="column is-7 is-centered buttonWrapper">
                                    {selectedAgency ? (
                                        <ContinueButton
                                            onClick={() => setSubmit(true)}
                                        />
                                    ) : null}
                                    <CancelButton />
                                </div>
                            </LoadingWrapper>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
